import { forwardRef, useId } from 'react'

import { useTranslation } from 'react-i18next'

import { HStack, Input, Text } from '@enechain/ecloud-designsystem'
import { FaLockRegular } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

type ImmutableInputProps = React.ComponentProps<typeof Input>

/**
 * OCCTO マスタ等で RHF の値を保持しつつ、readOnly 項目を「変更不可」として
 * 視覚的に示すための Input ラッパー。
 *
 * `readOnly` が true のとき disabled 同等のスタイルと、南京錠 + 「変更不可」
 * の補足ヒントを表示する (design/CLAUDE.md の .form-lock-hint 規約相当)。
 */
export const ImmutableInput = forwardRef<HTMLInputElement, ImmutableInputProps>(
  (props, ref): React.ReactElement => {
    const { t } = useTranslation()
    const hintId = useId()
    const isLocked = props.readOnly === true
    const describedBy =
      [props['aria-describedby']?.toString(), isLocked ? hintId : undefined]
        .filter(Boolean)
        .join(' ') || undefined

    return (
      <>
        <Input
          {...props}
          ref={ref}
          color={isLocked ? 'Text.disabled' : props.color}
          background={isLocked ? 'Surface.tertiary' : props.background}
          cursor={isLocked ? 'not-allowed' : props.cursor}
          aria-describedby={describedBy}
        />
        {isLocked && (
          <HStack
            alignItems="center"
            gap={1}
            marginTop={1}
            color="Text.midEmphasis"
            id={hintId}
          >
            <FaLockRegular boxSize={iconSize.fieldMessage} />
            <Text as="span" fontSize="sm">
              {t('common:form.immutableHint')}
            </Text>
          </HStack>
        )}
      </>
    )
  },
)

ImmutableInput.displayName = 'ImmutableInput'
